import { z } from 'zod';
import { AnalyticsPlatform, analyticsPlatformSchema } from '@common/interfaces/platform/AnalyticsPlatform';
import { ICommonEventFields, ICommonMCEvent } from '@common/interfaces/eventEntries/CommonEvent';
import {
  EventEntryBasketItem,
  eventEntryBasketItemSchema,
} from '@common/interfaces/eventEntries/EventEntryBasketItem';

export const START_CHECKOUT = 'start_checkout' as const;

/**
 * Client DTO for the StartCheckoutEvent to be emitted by the passport app or portal frontend.
 *
 * Does not contain identity information as this will come from the JWT before enqueueing.
 */
export interface IStartCheckoutClientDTO {
  /**
   * The platform from which the event was emitted.
   */
  platform: AnalyticsPlatform;
  /**
   * ISO formatted datetime for the event
   */
  timestamp: string;
  /**
   * Value of the invoice in GBP
   */
  invoiceValue: number;
  /**
   * List of items in the user's basket at the point of starting checkout.
   */
  basketItems: EventEntryBasketItem[];
}

/**
 * Based on IStartCheckoutClientDTO, but contains the identity of the user as verified using their credentials in the
 * portal backend
 */
export interface IStartCheckoutQueueDTO extends IStartCheckoutClientDTO {
  identity: {
    email: string;
  };
}

export const startCheckoutClientDTOSchema = z.object({
  platform: analyticsPlatformSchema,
  timestamp: z.string(),
  invoiceValue: z.number(),
  basketItems: z.array(eventEntryBasketItemSchema),
}) satisfies z.ZodType<IStartCheckoutClientDTO>;

export const startCheckoutQueueDTOSchema = z.object({
  platform: analyticsPlatformSchema,
  timestamp: z.string(),
  invoiceValue: z.number(),
  identity: z.object({
    email: z.string(),
  }),
  basketItems: z.array(eventEntryBasketItemSchema),
}) satisfies z.ZodType<IStartCheckoutQueueDTO>;

/**
 * The domain object for the entry event for "start checkout".
 *
 * This is fully specified and should be created in the worker by performing the needed lookups just before
 * reporting to SF:MC
 */
export interface IStartCheckoutDO extends ICommonEventFields {
  invoiceValue: number;
  /**
   * List of items in the user's basket at the point of starting checkout.
   */
  basketItems: EventEntryBasketItem[];
}

/**
 * The formatted event to be sent to SF:MC in the schema they desire
 */
export interface IStartCheckoutMCEvent extends ICommonMCEvent {
  /**
   * The total value of the invoice
   */
  InvoiceValue: number;

  /**
   * JSON formatted string containing the items in the user's basket
   */
  Bookings: string | null;
  /**
   * JSON formatted string containing the items in the user's basket
   */
  Products: string | null;
  /**
   * JSON formatted string containing the items in the user's basket
   */
  Events: string | null;
  /**
   * JSON formatted string containing the items in the user's basket
   */
  Contracts: string | null;
  /**
   * JSON formatted string containing the items in the user's basket
   */
  Courses: string | null;
}
